import convert from "convert-units";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import submit from "submit-form";
import { PARAMETER_OPTIONS } from "../../../../../constants";
import i18n from "../../../../../i18n";
import addMetadata from "../../../../../utils/add-metadata";
import { joinRpeForEdit, splitRpeText } from "../../../../../utils/rpe-range";
import { hasAssistantParameterValue } from "../../../../../utils/exercise-validations";

const { FREEFORM } = PARAMETER_OPTIONS;

const insertAtIndex = (arr, index, item) => [
  ...arr.slice(0, index),
  item,
  ...arr.slice(index),
];

/**
 * Per-day "quick edit" state + handlers, ported verbatim from the month's
 * MonthDay class (js/views/athlete-portal/calendar/components/MonthDay.js) so the
 * week view edits a day exactly the way the month does — same temp-exercise
 * model, same rep-range/complex/AMRAP normalization, same /actions/exercise
 * endpoints. Kept as a hook because WeekDay is a function component; MonthDay is
 * intentionally left untouched to avoid regressing the month.
 *
 * @param {object} args
 * @param {object} args.exercises  the day's scheduled exercises (canonical)
 * @param {string} args.dayString  DATE_DAY_FORMAT key for the day
 * @param {object} args.locals     rill locals (activeUser, activeUserToRequest, athlete)
 * @param {function} args.t        i18n translator
 */
export default function useDayQuickEdit({ exercises = [], dayString, locals, t }) {
  const [editDayMode, setEditDayMode] = useState(false);
  const [tempExercises, setTempExercises] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [showAddExercise, setShowAddExercise] = useState(false);

  // Keep the latest canonical exercises available to handlers without making
  // every callback depend on them (mirrors reading this.props in the class).
  const exercisesRef = useRef(exercises);
  exercisesRef.current = exercises;

  // When the underlying day data changes (week nav / post-save refresh), drop
  // edit mode so the card re-renders against the canonical exercises.
  useEffect(() => {
    setEditDayMode(false);
    setTempExercises([]);
  }, [exercises]);

  const displayError = (error = "") => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        error: typeof error === "string" ? error : error?.message || "",
      },
    });
  };

  const editSet = ({ newValue, otherNew, exerciseId, setIdx }) => {
    const { field, value } = newValue;
    setTempExercises((prev) =>
      prev.map((exercise) => {
        if (exercise._id === exerciseId) {
          const sets = exercise.sets.map((set, idy) => {
            if (parseInt(idy, 10) === parseInt(setIdx, 10)) {
              const setModified = {
                [field]: value,
                ...(otherNew && otherNew.value !== "NaN"
                  ? { [otherNew.field]: otherNew.value }
                  : {}),
              };
              return { ...set, ...setModified };
            }
            return set;
          });
          return { ...exercise, sets };
        }
        return exercise;
      })
    );
  };

  const handleInput = ({
    value,
    field,
    onlyNumber = false,
    exerciseId,
    setIdx = false,
    otherNew = false,
  }) => {
    if (
      (onlyNumber && Number.isNaN(value)) ||
      (field === "reps" &&
        value &&
        !value.includes("-") &&
        Number.isNaN(value)) ||
      (field === "sets" && Number.isNaN(value))
    )
      return displayError(t("calendar.monthDay.errors.onlyNumbers"));

    if (!setIdx && setIdx !== 0) {
      setTempExercises((prev) =>
        prev.map((exercise) => {
          if (exercise._id === exerciseId) {
            return { ...exercise, [field]: value };
          }
          return exercise;
        })
      );
      return;
    }

    const finalIdx =
      typeof setIdx === "string" && setIdx.length
        ? setIdx.split("-")[1]
        : setIdx;

    editSet({
      newValue: { field, value: onlyNumber ? parseFloat(value) : value },
      exerciseId,
      setIdx: finalIdx,
      otherNew,
    });
  };

  const handleSelectChange = (value, exerciseId) => {
    if (!value) return;
    const { movement, newPrimary, repMaximum } = value;
    const { units } = locals.activeUser;

    setTempExercises((prev) =>
      prev.map((exercise) => {
        if (exercise._id === exerciseId) {
          addMetadata(
            exercise,
            movement,
            newPrimary,
            locals.activeUserToRequest.primaryExercises,
            locals.activeUserToRequest.exerciseLibrary
          );

          if (value.newPrimary && value.newPrimary !== "assistant") {
            exercise.primary = value.newPrimary;
            exercise.secondary = value.movement;
            exercise.secondaryRepMaximum = value.repMaximum;
            exercise.type = "secondary";
            exercise.parameters = "null";
          } else if (value.newPrimary && value.newPrimary === "assistant") {
            exercise.primary = value.newPrimary;
            exercise.secondary = value.movement;
            exercise.secondaryRepMaximum = 0;
            exercise.type = "assistant";
            if (value.parameters) exercise.parameters = value.parameters;
          } else {
            exercise.primary = value.movement;
            exercise.secondary = "null";
            exercise.type = "primary";
            exercise.secondaryRepMaximum = 0;
            exercise.parameters = "null";
          }
          exercise.sets.forEach((set) => {
            const newLoadValue = _.round(
              (repMaximum * set.percentRepMax) / 100,
              2
            );
            if (units === "imperial") {
              set.load = _.round(convert(newLoadValue).from("kg").to("lb"), 2);
            }
            set.load = newLoadValue;
          });
        }
        return exercise;
      })
    );
  };

  const removeSet = (exerciseId, setIdx) => {
    const actualExercises = tempExercises;
    const result = actualExercises.find((ex) => ex._id === exerciseId);
    if (result.sets.length === 1) {
      if (result.complex && result.exercises) {
        submit("/actions/complex/remove", {
          method: "POST",
          body: { exercises: result.exercises },
        });
      } else {
        submit(`/actions/exercise/remove/${result._id}`, {
          method: "GET",
          body: { proxyAthlete: locals.athlete._id },
        });
      }
    } else {
      setTempExercises((prev) =>
        prev.map((exercise) => {
          if (
            exercise._id === exerciseId ||
            (result?.complex &&
              result?.complex === exercise.complex &&
              result?.complexInstance === exercise.complexInstance)
          ) {
            return {
              ...exercise,
              sets: exercise.sets.filter(
                (_set, idx) => parseInt(idx, 10) !== parseInt(setIdx, 10)
              ),
            };
          }
          return exercise;
        })
      );
    }
  };

  const removeTempSet = (exerciseId, setIdx) => {
    const result = tempExercises.find((ex) => ex._id === exerciseId);
    setTempExercises((prev) =>
      prev.map((exercise) => {
        if (
          exercise._id === exerciseId ||
          (result?.complex &&
            result?.complex === exercise.complex &&
            result?.complexInstance === exercise.complexInstance)
        ) {
          return {
            ...exercise,
            sets: exercise.sets.filter(
              (_set, idx) => parseInt(idx, 10) !== parseInt(setIdx, 10)
            ),
          };
        }
        return exercise;
      })
    );
  };

  const addTempSet = ({ exerciseId, pos = false, freeForm = false }) => {
    const exercisesCopy = [...tempExercises];
    const result = exercisesCopy.find((_ex) => _ex._id === exerciseId);
    const finalPos = pos
      ? parseInt(pos, 10) + 1
      : result
        ? result.sets.length
        : -1;
    setTempExercises(
      exercisesCopy.map((tempExercise) => {
        if (
          tempExercise._id === exerciseId ||
          (result?.complex &&
            result?.complex === tempExercise.complex &&
            result?.complexInstance === tempExercise.complexInstance)
        ) {
          const objectToInsert = freeForm
            ? {
                percentRepMax: 0,
                equipped: false,
                zone: "deload",
                freeform: "",
                temp: true,
              }
            : result.primary !== "assistant"
              ? {
                  load: "",
                  sets: "",
                  reps: "",
                  rpe: "",
                  percentRepMax: "",
                  equipped: false,
                  zone: "deload",
                  temp: true,
                }
              : { distance: "", temp: true };
          const sets = insertAtIndex(tempExercise.sets, finalPos, objectToInsert);
          return { ...tempExercise, sets };
        }
        return tempExercise;
      })
    );
  };

  // New exercises aren't created inline in the week (handled via the add modal),
  // so there are no pending "temp" exercises to look up — match the month's API
  // shape by always returning false.
  const getTempExercise = () => false;

  const handleSuperset = (mode = "add", { fullExercise, nextExerciseId }) => {
    if (mode === "remove") {
      setTempExercises((prev) =>
        prev.map((elem) => {
          if (fullExercise._id === elem._id) delete elem.nextSuperset;
          return elem;
        })
      );
    }
    submit(`/actions/exercise/superset/${mode}`, {
      method: "POST",
      body: {
        exercise: fullExercise._id,
        nextExercise: nextExerciseId,
        fullExercise,
        monthCalendar: true,
      },
    });
  };

  const toggleEditDayMode = () => {
    if (!editDayMode) {
      const next = _.cloneDeep(exercisesRef.current).map((exercise) => {
        exercise.sets.forEach((set) => {
          if (set.reps && set.repsLower) {
            set.reps = `${set.repsLower}-${set.reps}`;
          }
          joinRpeForEdit(set);
        });
        return exercise;
      });
      setTempExercises(next);
      setEditDayMode(true);
    }
  };

  const resetExercises = () => {
    setEditDayMode(false);
    setTempExercises([]);
  };

  const removeTempExercise = (id, sameList, fromExercises) => {
    if (!sameList) {
      setTempExercises((prev) => prev.filter((ex) => ex._id !== id));
    } else {
      setTempExercises((prev) =>
        fromExercises
          .map((exId) => prev.find((ex) => ex._id === exId))
          .filter(Boolean)
      );
    }
  };

  const saveEditedExercise = (athleteId) => {
    try {
      const exerciseWithoutTemp = tempExercises.map((tempExercise) => {
        const { type, parameters, sets, primary, secondary } = tempExercise;

        let firstComplexExercise;
        if (
          tempExercise.complex &&
          tempExercise.complexInstance &&
          tempExercise.indexInComplex !== 0
        ) {
          firstComplexExercise = tempExercises.find(
            (ex) =>
              ex.complex === tempExercise.complex &&
              ex.complexInstance === tempExercise.complexInstance &&
              ex.indexInComplex === 0
          );
        }

        const setsModified = sets.map((editedSet, idx) => {
          // Work on a copy: a blocked save must leave the cells as typed.
          const set = { ...editedSet };
          if (set.temp) delete set.temp;

          const isRepRange = set.reps && String(set.reps).includes("-");
          if (isRepRange) {
            if (!set.reps.match(/^\d{1,4}-?\d{1,4}$/)) {
              throw new Error(
                t("calendar.monthDay.errors.repsRangeInvalid")
              );
            }
            const [repsLower, reps] = set.reps.split("-");
            set.reps = reps || repsLower;
            if (reps) {
              set.repsLower = repsLower;
            }
          }
          if (!isRepRange && set.repsLower) set.repsLower = null;

          if (firstComplexExercise) {
            set.sets = firstComplexExercise.sets?.[idx]?.sets || set.sets;
            set.rpe = firstComplexExercise.sets?.[idx]?.rpe || set.rpe;
            set.rpeLower = firstComplexExercise.sets?.[idx]?.rpe
              ? firstComplexExercise.sets[idx].rpeLower
              : set.rpeLower;
            set.load = firstComplexExercise.sets?.[idx]?.load || set.load;
            set.loadDrop =
              firstComplexExercise.sets?.[idx]?.loadDrop || set.loadDrop;
          }

          const rpeError = splitRpeText(set);
          if (rpeError) throw new Error(i18n.t(`common:${rpeError}`));

          if (type !== "assistant") {
            const findChain = (field) =>
              set[field]
                ? set[field] === 0 ||
                  set[field] === "0" ||
                  set[field] === undefined ||
                  set[field] === "" ||
                  set[field] === null
                : true;

            const resultChain = {
              reps: findChain("reps"),
              sets: findChain("sets"),
              rpe: findChain("rpe"),
              load: findChain("load"),
              loadDrop: findChain("loadDrop"),
            };

            const isAmrap = set.rpe === "AMRAP";
            const errorFound =
              (resultChain.reps && !isAmrap) || resultChain.sets;

            if (errorFound)
              throw new Error(
                t("calendar.monthDay.errors.repsSetsRequired")
              );
          } else if (
            parameters &&
            !parameters.includes(FREEFORM) &&
            !hasAssistantParameterValue(set, parameters)
          ) {
            const setsOrRounds = parameters.includes("Sets")
              ? "sets"
              : parameters.includes("Rounds")
                ? "rounds"
                : false;
            if (setsOrRounds) set[setsOrRounds] = 1;
            else
              throw new Error(
                t("calendar.monthDay.errors.parametersRequired")
              );
          }
          return set;
        });

        let movementLibrary = {};
        if (secondary) {
          const libraryCoach = _.get(
            locals.activeUser,
            `exerciseLibrary.${primary}`,
            []
          );
          const existsCoach = _.find(libraryCoach, { movement: secondary });
          if (existsCoach) {
            existsCoach.primary = primary;
            movementLibrary = existsCoach;
          }
        }

        if (tempExercise._tempoChecked && !tempExercise.tempo?.trim()) {
          throw new Error("Please enter the tempo value");
        }
        delete tempExercise._tempoChecked;

        if (tempExercise.notes === "") {
          tempExercise.notes = " ";
        }
        delete tempExercise.order;

        return { ...tempExercise, sets: setsModified, movementLibrary };
      });

      submit(
        `/actions/exercise/quick-edit?proxyAthlete=${athleteId}&reqSource=calendar`,
        {
          method: "POST",
          body: { exercises: exerciseWithoutTemp },
        }
      );
      setEditDayMode(false);
      setTempExercises(exerciseWithoutTemp);
    } catch (err) {
      return displayError(err?.message ?? err);
    }
  };

  // Passthrough so the month's DayEditor children that call setState({ ... })
  // (e.g. the empty-day add/paste hover) keep working.
  const setState = (patch) => {
    if (!patch || typeof patch !== "object") return;
    if ("showAddExercise" in patch) setShowAddExercise(patch.showAddExercise);
    if ("selectedExercise" in patch) setSelectedExercise(patch.selectedExercise);
    if ("showOptions" in patch) setShowOptions(patch.showOptions);
  };

  return {
    editDayMode,
    tempExercises,
    selectedExercise,
    showOptions,
    showAddExercise,
    setState,
    toggleEditDayMode,
    resetExercises,
    saveEditedExercise,
    handleInput,
    addTempSet,
    removeTempSet,
    removeSet,
    handleSelectChange,
    handleSuperset,
    getTempExercise,
    removeTempExercise,
  };
}
